/**
 * The two providers that have to sit outside the router: NextUI's system
 * provider and the theme provider.
 *
 * `NextUIProvider` takes a `navigate` so its `Link`-backed components push onto
 * the SPA history instead of doing a full page load. In the Next.js original
 * that was `router.push`; here it is react-router's `navigate`, which is why
 * this component must render INSIDE the router, not above it.
 */
import { NextUIProvider } from "@nextui-org/system";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import type { ThemeProviderProps } from "next-themes/dist/types";
import * as React from "react";
import { useHref, useNavigate } from "react-router-dom";

export interface ProvidersProps {
  children: React.ReactNode;
  themeProps?: ThemeProviderProps;
}

export function Providers({ children, themeProps }: ProvidersProps) {
  const navigate = useNavigate();

  return (
    // `useHref` is passed alongside `navigate` so NextUI renders hrefs that
    // respect a non-root basename; without it an anchor's href and the actual
    // navigation can disagree.
    <NextUIProvider navigate={navigate} useHref={useHref}>
      <NextThemesProvider {...themeProps}>{children}</NextThemesProvider>
    </NextUIProvider>
  );
}
